/**
 * Server-side paging, sorting and filtering request model shared by every list screen (ADR-0010/D-6). Serialised as
 * `page[size]`, `page[number]` or `page[after]`, `sort=field,-field`, `filter[field][op]=value`, `includeTotal=true`.
 * The server whitelists fields and operators per endpoint; the client never builds query text.
 */

export const filterOperators = [
  "eq",
  "ne",
  "lt",
  "lte",
  "gt",
  "gte",
  "in",
  "contains",
  "startsWith",
  "between",
] as const;
export type FilterOperator = (typeof filterOperators)[number];

export type FilterScalar = string | number | boolean;

export interface FilterSpec {
  field: string;
  op: FilterOperator;
  /** Scalar for most operators, a list for `in`, a `[from, to]` pair for `between`. */
  value: FilterScalar | readonly FilterScalar[];
}

export type SortDirection = "asc" | "desc";

export interface SortSpec {
  field: string;
  direction: SortDirection;
}

export type PageSpec = { size: number; number: number } | { size: number; after: string | null };

export interface ListQuery {
  page: PageSpec;
  sort: readonly SortSpec[];
  filters: readonly FilterSpec[];
  /** Ask the server for `totalCount` (costs a count query; list reports need it for the pager). */
  includeTotal: boolean;
}

/** Response envelope of list endpoints. */
export interface PagedResult<T> {
  items: T[];
  /** Present when `includeTotal` was requested. */
  totalCount?: number;
  /** Cursor for the next page in cursor mode; `null` on the last page. */
  nextCursor?: string | null;
}

export const maxPageSize = 500;
export const defaultPageSize = 50;

export function createListQuery(overrides: Partial<ListQuery> = {}): ListQuery {
  return {
    page: { size: defaultPageSize, number: 1 },
    sort: [],
    filters: [],
    includeTotal: true,
    ...overrides,
  };
}

function clampSize(size: number): number {
  if (!Number.isFinite(size) || size < 1) return 1;
  return Math.min(Math.trunc(size), maxPageSize);
}

function scalar(value: FilterScalar): string {
  return typeof value === "string" ? value : String(value);
}

function filterValue(filter: FilterSpec): string {
  const { op, value } = filter;
  if (op === "in" || op === "between") {
    const list = Array.isArray(value) ? (value as readonly FilterScalar[]) : [value as FilterScalar];
    if (op === "between" && list.length !== 2) {
      throw new RangeError(`Filter "${filter.field}" with "between" needs exactly two values`);
    }
    return list.map(scalar).join(",");
  }
  if (Array.isArray(value)) {
    throw new RangeError(`Filter "${filter.field}" with "${op}" takes a single value`);
  }
  return scalar(value as FilterScalar);
}

/** Serialises a list query to URL search parameters (ADR-0010/D-6). Empty filter values are skipped. */
export function toSearchParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set("page[size]", String(clampSize(query.page.size)));
  if ("number" in query.page) {
    params.set("page[number]", String(Math.max(1, Math.trunc(query.page.number))));
  } else if (query.page.after) {
    params.set("page[after]", query.page.after);
  }
  if (query.sort.length > 0) {
    params.set("sort", query.sort.map((s) => (s.direction === "desc" ? `-${s.field}` : s.field)).join(","));
  }
  for (const filter of query.filters) {
    if (filter.value === "" || (Array.isArray(filter.value) && filter.value.length === 0)) continue;
    params.append(`filter[${filter.field}][${filter.op}]`, filterValue(filter));
  }
  if (query.includeTotal) {
    params.set("includeTotal", "true");
  }
  return params;
}

/** Stable key fragment for TanStack Query (plain JSON, order-preserving). */
export function listQueryKey(query: ListQuery): readonly unknown[] {
  return [
    "list",
    { page: query.page, sort: query.sort, filters: query.filters, includeTotal: query.includeTotal },
  ] as const;
}

/** Changes page number while keeping everything else. */
export function withPage(query: ListQuery, number: number): ListQuery {
  return { ...query, page: { size: query.page.size, number } };
}

/** Replaces the filters and goes back to the first page. */
export function withFilters(query: ListQuery, filters: readonly FilterSpec[]): ListQuery {
  return { ...query, filters, page: resetPage(query.page) };
}

/** Replaces the sort order and goes back to the first page. */
export function withSort(query: ListQuery, sort: readonly SortSpec[]): ListQuery {
  return { ...query, sort, page: resetPage(query.page) };
}

/** Changes page size and goes back to the first page. */
export function withPageSize(query: ListQuery, size: number): ListQuery {
  const clamped = clampSize(size);
  return { ...query, page: "number" in query.page ? { size: clamped, number: 1 } : { size: clamped, after: null } };
}

function resetPage(page: PageSpec): PageSpec {
  return "number" in page ? { size: page.size, number: 1 } : { size: page.size, after: null };
}

/** Number of pages for a known total; at least 1. */
export function pageCount(totalCount: number, pageSize: number): number {
  return Math.max(1, Math.ceil(totalCount / clampSize(pageSize)));
}
